<template>
  <div class="bulk-shipment-import">
    <div class="card rounded-0 shadow-sm border-bottom py-2 mb-0 d-flex">
      <div class="d-flex align-items-center justify-content-between px-2">
        <div class="d-flex align-items-center">
          <b-button variant="link" class="p-0 mr-2 text-body" :to="{ name: 'create_consignment' }">
            <feather-icon icon="XIcon" size="24" />
          </b-button>
          <div>
            <h3 class="mb-0 font-weight-bolder">{{ $t('Bulk Shipment with Excel') }}</h3>
            <small class="text-muted">{{ $t('Create multiple shipments easily') }}</small>
          </div>
        </div>
        <span class="font-weight-bolder text-muted">{{ $t('Step') }} {{ currentStep }} / 3</span>
      </div>
    </div>

    <div
      class="card rounded-0 shadow-sm border-bottom mb-2 py-2 d-flex align-items-center justify-content-center"
    >
      <div class="progress-stepper-wrap px-2">
        <div class="progress-stepper">
          <div
            v-for="step in stepperSteps"
            :key="`step-${step.number}`"
            class="progress-stepper__segment"
          >
            <div
              class="progress-stepper__item"
              :style="{ cursor: step.number < currentStep ? 'pointer' : 'default' }"
              @click="goToStep(step.number)"
            >
              <div
                class="progress-stepper__badge"
                :class="`progress-stepper__badge--${getStepStatus(step.number)}`"
              >
                <feather-icon
                  v-if="getStepStatus(step.number) === 'completed'"
                  icon="CheckIcon"
                  size="14"
                />
                <span v-else>{{ step.number }}</span>
              </div>
              <div
                class="progress-stepper__label"
                :class="`progress-stepper__label--${getStepStatus(step.number)}`"
              >
                {{ step.label }}
              </div>
            </div>
            <div
              v-if="step.number !== stepperSteps.length"
              class="progress-stepper__line"
              :class="{
                'progress-stepper__line--completed': currentStep > step.number
              }"
            ></div>
          </div>
        </div>
      </div>
    </div>

    <div
      class="mx-auto"
      :style="currentStep === 3 ? 'max-width: 100%; padding: 0 40px;' : 'max-width: 900px;'"
      prefix="bulk_shipment_import"
    >
      <div class="py-2">
        <div v-if="currentStep === 1" class="sender-step card border-0 shadow-sm overflow-hidden">
          <b-overlay
            :show="isLoadingSenderStep"
            rounded
            opacity="0.7"
            spinner-variant="primary"
            variant="transparent"
            overlay-class="custom-overlay-bg"
          >
            <div class="sender-step__header d-flex flex-column flex-lg-row justify-content-between">
              <div class="d-flex align-items-start">
                <div
                  class="sender-step__icon d-flex align-items-center justify-content-center flex-shrink-0"
                >
                  <feather-icon icon="PackageIcon" size="24" class="text-warning" />
                </div>
                <div class="ml-2">
                  <h2 class="mb-50 font-weight-bolder">{{ $t('Sender Address') }}</h2>
                  <p class="text-muted mb-0">
                    {{ $t('All shipments will be sent from this address') }}
                  </p>
                </div>
              </div>

              <div class="sender-mode-toggle mt-2 mt-lg-0">
                <button
                  type="button"
                  class="sender-mode-toggle__button"
                  :class="{
                    'sender-mode-toggle__button--active': senderAddressMode === 'registered'
                  }"
                  @click="switchSenderAddressMode('registered')"
                >
                  {{ $t('Saved Address') }}
                </button>
                <button
                  type="button"
                  class="sender-mode-toggle__button"
                  :class="{ 'sender-mode-toggle__button--active': senderAddressMode === 'manual' }"
                  @click="switchSenderAddressMode('manual')"
                >
                  {{ $t('Manual Entry') }}
                </button>
              </div>
            </div>

            <div class="sender-step__body">
              <bulk-shipment-sender-address-editor
                ref="senderStepEditor"
                :sender-address-mode="senderAddressMode"
                :has-loaded-registered-addresses="hasLoadedRegisteredAddresses"
                :registered-address-options="registeredAddressOptions"
                :selected-registered-address="selectedRegisteredAddress"
                :selected-registered-address-id="selectedRegisteredAddressId"
                :show-registered-address-selector="showRegisteredAddressSelector"
                :countries="countries"
                :is-loading-countries="isLoadingCountries"
                :manual-sender-address="manualSenderAddress"
                :is-default-registered-address="isDefaultRegisteredAddress"
                :get-registered-address-title="getRegisteredAddressTitle"
                :get-registered-address-line="getRegisteredAddressLine"
                :get-registered-address-location="getRegisteredAddressLocation"
                :get-registered-address-phone="getRegisteredAddressPhone"
                @toggle-registered-selector="toggleRegisteredAddressSelector"
                @select-registered-address="selectRegisteredAddress"
                @ensure-countries-loaded="ensureCountriesLoaded"
                @manual-country-change="handleManualSenderCountryChange"
                @manual-address-change="handleManualSenderAddressChange"
                @open-postal-code-search="openSenderPostalCodeSearch"
              />
            </div>

            <div class="sender-step__footer d-flex justify-content-end">
              <b-button variant="primary" class="px-3" @click="handleSenderStepContinue">
                {{ $t('Continue') }} <feather-icon icon="ArrowRightIcon" class="ml-50" />
              </b-button>
            </div>
          </b-overlay>
        </div>

        <div v-else-if="currentStep === 2" class="mx-auto" style="width: 900px">
          <div class="card border-0 shadow-sm p-3">
            <div class="mb-2 d-inline-flex align-items-center rounded">
              <feather-icon icon="InfoIcon" size="24" class="text-warning mr-1" />
              <div class="text-left">
                <h5 class="font-weight-bolder mb-0">
                  {{ $t('How should I fill the Excel?') }}
                </h5>
                <small>{{ $t('Please pay attention to the following basic rules:') }}</small>
              </div>
            </div>

            <div class="mb-1 font-weight-bold">
              <feather-icon icon="CheckSquareIcon" class="text-success mr-1" />
              <span>{{ $t('Each row represents 1 shipment') }}</span>
            </div>
            <div class="mb-1 font-weight-bold">
              <feather-icon icon="CheckSquareIcon" class="text-success mr-1" />
              <span>{{
                $t('Reference number field is mandatory and must be unique for each shipment')
              }}</span>
            </div>
            <div class="mb-1 font-weight-bold">
              <feather-icon icon="CheckSquareIcon" class="text-success mr-1" />
              <span>{{ $t('Maximum 100 shipments can be uploaded at once') }}</span>
            </div>
            <div class="mb-1">
              <feather-icon icon="CheckSquareIcon" class="text-success mr-1" />
              <span>{{ $t('Mandatory fields cannot be empty') }}</span>
            </div>
            <div class="mb-1">
              <feather-icon icon="CheckSquareIcon" class="text-success mr-1" />
              <span>{{ $t('Column names in the template should not be changed') }}</span>
            </div>

            <p class="mt-2 small mb-2">
              {{ $t('System checks data after upload and shows you incorrect rows to edit.') }}
            </p>

            <b-button variant="warning" class="font-weight-bold" @click="downloadTemplate">
              <feather-icon icon="DownloadIcon" class="mr-50" />
              {{ $t('Download Excel Template') }}
            </b-button>
          </div>

          <div
            v-if="!file || uploadError || isParsing"
            class="d-flex flex-column align-items-center justify-content-center py-5 border rounded card"
            style="border: 2px dashed #d8d6de; min-height: 300px; position: relative"
            @dragover.prevent
            @drop.prevent="handleFile"
          >
            <b-overlay
              :show="isParsing"
              no-wrap
              rounded="sm"
              opacity="0.8"
              spinner-variant="primary"
              variant="transparent"
              overlay-class="custom-overlay-bg"
            />

            <div class="bg-light-primary rounded-circle p-2 mb-2">
              <feather-icon icon="UploadCloudIcon" size="32" class="text-primary" />
            </div>
            <h5 class="font-weight-bold">{{ $t('Upload Excel File') }}</h5>
            <p class="text-muted mb-2">
              {{ $t('Drag and drop or select file') }}
              <a
                href="javascript:void(0)"
                class="font-weight-bold"
                @click="$refs.fileInput.$el.click()"
                >{{ $t('Select File') }}</a
              >
            </p>
            <p class="text-muted small">{{ $t('Supported formats: .xlsx') }}</p>

            <b-form-file
              ref="fileInput"
              v-model="file"
              accept=".xlsx"
              plain
              class="d-none"
              @change="handleFile"
            />
          </div>

          <div
            v-else
            class="d-flex flex-column align-items-center justify-content-center py-5 border rounded card"
            style="border: 2px dashed #28c76f !important; min-height: 300px"
          >
            <div class="bg-light-success rounded-circle p-2 mb-2">
              <feather-icon icon="CheckCircleIcon" size="32" class="text-success" />
            </div>

            <h5 class="font-weight-bold text-success">{{ file.name }}</h5>
            <p class="text-muted mb-2">{{ $t('File uploaded successfully') }}</p>

            <b-button
              variant="flat-danger"
              size="sm"
              class="font-weight-bold mt-1"
              @click="clearFile"
            >
              <feather-icon icon="Trash2Icon" class="mr-50" /> {{ $t('Remove File') }}
            </b-button>
          </div>

          <b-alert v-if="uploadError" show variant="danger" class="mt-2">
            <div class="alert-body">
              <feather-icon icon="AlertCircleIcon" class="mr-50" />
              <span>{{ uploadError }}</span>
            </div>
          </b-alert>

          <div class="d-flex justify-content-between mt-2 mb-3">
            <b-button variant="outline-secondary" class="bulk-soft-button" @click="prevStep">
              <feather-icon icon="ArrowLeftIcon" class="mr-50" /> {{ $t('Back') }}
            </b-button>

            <b-button variant="primary" :disabled="!file || !!uploadError" @click="currentStep = 3">
              {{ $t('Review & Create') }}
              <feather-icon icon="ArrowRightIcon" class="ml-50" />
            </b-button>
          </div>
        </div>

        <div v-else-if="currentStep === 3">
          <div class="card sender-summary-card mb-3">
            <div
              class="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center"
            >
              <div class="d-flex align-items-center">
                <div
                  class="registered-address-card__icon mr-2 d-flex align-items-center justify-content-center flex-shrink-0"
                >
                  <feather-icon icon="HomeIcon" size="22" class="text-primary" />
                </div>
                <div>
                  <small class="d-block mb-25">{{ $t('Sender Address') }}</small>
                  <div class="font-weight-bolder">{{ senderSummaryTitle }}</div>
                  <div class="">{{ senderSummaryDetails }}</div>
                  <small class="text-primary d-block mt-50">
                    {{ $t('Changes will be applied to all shipments') }}
                  </small>
                </div>
              </div>
              <b-button variant="outline-primary" class="mt-2 mt-lg-0" @click="openSenderEditModal">
                {{ $t('Edit') }}
              </b-button>
            </div>
          </div>

          <div class="review-toolbar card border-0 shadow-sm mb-2">
            <div
              class="review-toolbar__inner d-flex flex-column flex-xl-row justify-content-between align-items-xl-center"
            >
              <div class="d-flex align-items-center flex-wrap mb-1 mb-xl-0">
                <b-button
                  variant="outline-secondary"
                  size="sm"
                  class="mr-2 review-toolbar__back bulk-soft-button"
                  @click="currentStep = 2"
                >
                  <feather-icon icon="ArrowLeftIcon" class="mr-50" /> {{ $t('Back') }}
                </b-button>
                <div>
                  <h4 class="mb-0">{{ displayedRows.length }} {{ $t('Shipment') }}</h4>
                  <small class="text-muted">
                    {{ $t('You can open the edit panel by clicking the row') }}
                  </small>
                </div>
              </div>

              <div class="d-flex align-items-center flex-wrap justify-content-xl-end">
                <b-button
                  v-if="hasErrors"
                  variant="outline-danger"
                  size="sm"
                  class="mr-1 mb-1"
                  @click="showOnlyErrors = !showOnlyErrors"
                >
                  <feather-icon icon="FilterIcon" class="mr-50" />
                  {{ showOnlyErrors ? $t('Show All') : $t('Show Only Errors') }}
                </b-button>
                <b-button
                  variant="outline-primary"
                  size="sm"
                  class="mr-1 mb-1"
                  @click="selectAllDisplayedRows"
                >
                  {{ $t('Select All') }}
                </b-button>
                <b-button
                  variant="outline-secondary"
                  size="sm"
                  class="mr-1 mb-1"
                  @click="clearSelection"
                >
                  {{ $t('Clear Selection') }}
                </b-button>
                <b-button
                  variant="outline-success"
                  size="sm"
                  class="mr-1 mb-1"
                  :disabled="!downloadableRows.length"
                  @click="downloadAllLabels"
                >
                  {{ $t('Download Labels in Bulk') }} ({{ downloadableRows.length }})
                </b-button>
                <b-button
                  variant="primary"
                  size="sm"
                  class="mb-1"
                  :disabled="!creatableSelectedRows.length || submitting"
                  @click="submitSelected"
                >
                  <b-spinner v-if="submitting" small class="mr-50" />
                  {{ $t('Create Selected Shipments') }} ({{ creatableSelectedRows.length }})
                </b-button>
              </div>
            </div>
          </div>

          <div class="card border-0 shadow-sm overflow-hidden">
            <div class="table-responsive">
              <table class="table mb-0 bulk-review-table">
                <thead>
                  <tr>
                    <th class="bulk-review-table__checkbox-cell">
                      <b-form-checkbox
                        :checked="allDisplayedRowsSelected"
                        :indeterminate="isSelectionPartial"
                        @change="toggleSelectAllDisplayed"
                      />
                    </th>
                    <th class="bulk-review-table__toggle-cell"></th>
                    <th>{{ $t('Reference') }}</th>
                    <th>{{ $t('Service Provider') }}</th>
                    <th>{{ $t('Recipient Name') }}</th>
                    <th>{{ $t('Recipient Phone') }}</th>
                    <th>{{ $t('Country / City') }}</th>
                    <th>{{ $t('Weight') }}</th>
                    <th>{{ $t('Status') }}</th>
                    <th class="text-right">{{ $t('Action') }}</th>
                  </tr>
                </thead>

                <template v-if="displayedRows.length">
                  <tbody v-for="row in displayedRows" :key="row.id">
                    <tr
                      class="bulk-review-row"
                      :class="{
                        'bulk-review-row--expanded': expandedRowId === row.id,
                        'bulk-review-row--error': row.statusType === 'error'
                      }"
                      @click="toggleRowPanel(row.id)"
                    >
                      <td class="bulk-review-table__checkbox-cell" @click.stop>
                        <b-form-checkbox
                          :checked="isRowSelected(row.id)"
                          @change="checked => toggleRowSelection(row.id, checked)"
                        />
                      </td>
                      <td class="bulk-review-table__toggle-cell">
                        <feather-icon
                          :icon="expandedRowId === row.id ? 'ChevronDownIcon' : 'ChevronRightIcon'"
                          class="text-primary"
                        />
                      </td>
                      <td class="font-weight-bolder">{{ row.reference_no || '-' }}</td>
                      <td>{{ getProviderDisplayName(row.provider_code) }}</td>
                      <td>{{ row.consignee_name || '-' }}</td>
                      <td>{{ row.consignee_phone || '-' }}</td>
                      <td>
                        <div>{{ getCountryDisplayName(row.consignee_country) || '-' }}</div>
                        <small class="text-muted">{{ row.consignee_city || '-' }}</small>
                      </td>
                      <td>{{ formatWeight(row.weight) }}</td>
                      <td>
                        <b-badge :variant="getStatusBadgeVariant(row)" pill>
                          {{ getStatusBadgeText(row) }}
                        </b-badge>
                        <small
                          v-if="row.statusType === 'error' && row.status"
                          class="text-danger d-block mt-50"
                        >
                          {{ row.status }}
                        </small>
                      </td>
                      <td class="text-right" @click.stop>
                        <div class="d-inline-flex align-items-center flex-wrap justify-content-end">
                          <b-button
                            v-if="row.statusType === 'success'"
                            variant="outline-primary"
                            size="sm"
                            class="mr-50 mb-50"
                            @click="downloadLabel(row)"
                          >
                            {{ $t('Download Label') }}
                          </b-button>
                          <b-button
                            v-else-if="
                              row.statusType === 'error' ||
                              (row.statusType === 'pending' && row.hasAttempted)
                            "
                            :variant="row.statusType === 'error' ? 'danger' : 'primary'"
                            size="sm"
                            class="mr-50 mb-50"
                            @click="submitRow(row)"
                          >
                            {{ $t('Recreate') }}
                          </b-button>
                          <b-button
                            v-else
                            variant="flat-danger"
                            size="sm"
                            class="btn-icon rounded-circle mb-50"
                            @click="confirmRemoveRow(row.id)"
                          >
                            <feather-icon icon="Trash2Icon" />
                          </b-button>
                        </div>
                      </td>
                    </tr>

                    <tr v-if="expandedRowId === row.id" class="bulk-review-details-row">
                      <td colspan="10">
                        <div class="bulk-review-details">
                          <div
                            v-for="section in reviewSections"
                            :key="section.key"
                            class="bulk-review-section"
                          >
                            <div class="bulk-review-section__header">
                              <div class="d-flex align-items-center">
                                <feather-icon :icon="section.icon" class="mr-50 text-primary" />
                                <h5 class="mb-0">{{ $t(section.title) }}</h5>
                              </div>
                            </div>

                            <div class="row">
                              <div
                                v-for="field in section.fields"
                                :key="field.key"
                                :class="field.colClass || 'col-lg-4 col-md-6'"
                              >
                                <b-form-group class="mb-1">
                                  <label class="font-weight-bolder mb-50">
                                    {{ $t(field.label) }}
                                    <span v-if="field.required" class="text-danger">*</span>
                                  </label>

                                  <v-select
                                    v-if="field.type === 'country'"
                                    :value="getCountrySelectValue(row[field.key])"
                                    :options="countries"
                                    :clearable="false"
                                    :class="{ 'is-invalid': !!getRowFieldError(row, field.key) }"
                                    label="text"
                                    @input="
                                      option => handleCountryFieldChange(row, field.key, option)
                                    "
                                  />

                                  <b-form-select
                                    v-else-if="field.type === 'provider'"
                                    :value="normalizeSelectValue(row[field.key])"
                                    :options="providerSelectOptions"
                                    :class="getRowFieldClass(row, field.key)"
                                    @input="value => handleRowFieldChange(row, field.key, value)"
                                  />

                                  <b-form-select
                                    v-else-if="field.type === 'package_type'"
                                    :value="normalizeSelectValue(row[field.key])"
                                    :options="packageTypeOptions"
                                    :class="getRowFieldClass(row, field.key)"
                                    @input="value => handleRowFieldChange(row, field.key, value)"
                                  />

                                  <b-form-select
                                    v-else-if="field.type === 'post_type'"
                                    :value="normalizeSelectValue(row[field.key])"
                                    :options="postTypeOptions"
                                    :class="getRowFieldClass(row, field.key)"
                                    @input="value => handleRowFieldChange(row, field.key, value)"
                                  />

                                  <b-form-select
                                    v-else-if="field.type === 'incoterms'"
                                    :value="normalizeSelectValue(row[field.key])"
                                    :options="incotermsOptions"
                                    :class="getRowFieldClass(row, field.key)"
                                    @input="value => handleRowFieldChange(row, field.key, value)"
                                  />

                                  <b-form-select
                                    v-else-if="field.type === 'currency'"
                                    :value="normalizeCurrencyValue(row[field.key])"
                                    :options="currencyOptions"
                                    :class="getRowFieldClass(row, field.key)"
                                    @input="value => handleRowFieldChange(row, field.key, value)"
                                  />

                                  <b-form-textarea
                                    v-else-if="field.type === 'textarea'"
                                    :value="row[field.key]"
                                    rows="3"
                                    :class="getRowFieldClass(row, field.key)"
                                    @input="value => handleRowFieldChange(row, field.key, value)"
                                  />

                                  <b-form-input
                                    v-else
                                    :value="row[field.key]"
                                    :type="field.inputType || 'text'"
                                    :class="getRowFieldClass(row, field.key)"
                                    @input="value => handleRowFieldChange(row, field.key, value)"
                                  />

                                  <small
                                    v-if="getRowFieldError(row, field.key)"
                                    class="text-danger"
                                  >
                                    {{ getRowFieldError(row, field.key) }}
                                  </small>
                                </b-form-group>
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </template>

                <tbody v-else>
                  <tr>
                    <td colspan="10" class="text-center text-muted py-3">
                      {{ $t('No records found') }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <postal-code-search-modal
      ref="postalCodeSearchModal"
      modal-id="bulk-import-postal-code-search"
      :country-iso2="senderPostalCodeCountryIso2"
      @select="handleSenderPostalCodeSelect"
    />

    <b-modal
      v-model="showSenderEditModal"
      size="lg"
      hide-footer
      modal-class="bulk-import-modal"
      @hidden="handleSenderEditModalHidden"
    >
      <template #modal-header="{ close }">
        <div
          class="w-100 d-flex flex-column flex-lg-row align-items-start align-items-lg-center justify-content-between"
          style="gap: 12px"
        >
          <h5 class="modal-title mb-0">{{ $t('Edit Sender Address') }}</h5>

          <div class="d-flex align-items-center" style="gap: 12px">
            <div class="sender-mode-toggle">
              <button
                type="button"
                class="sender-mode-toggle__button"
                :class="{
                  'sender-mode-toggle__button--active': senderAddressMode === 'registered'
                }"
                @click="switchSenderAddressMode('registered')"
              >
                {{ $t('Saved Address') }}
              </button>
              <button
                type="button"
                class="sender-mode-toggle__button"
                :class="{ 'sender-mode-toggle__button--active': senderAddressMode === 'manual' }"
                @click="switchSenderAddressMode('manual')"
              >
                {{ $t('Manual Entry') }}
              </button>
            </div>

            <b-button variant="link" class="p-0 text-muted" @click="close()">
              <feather-icon icon="XIcon" size="20" />
            </b-button>
          </div>
        </div>
      </template>

      <div class="mb-2">
        <small class="text-muted">
          {{ $t('The information you save will be applied to all shipments') }}
        </small>
      </div>

      <bulk-shipment-sender-address-editor
        ref="senderEditModalEditor"
        :sender-address-mode="senderAddressMode"
        :has-loaded-registered-addresses="hasLoadedRegisteredAddresses"
        :registered-address-options="registeredAddressOptions"
        :selected-registered-address="selectedRegisteredAddress"
        :selected-registered-address-id="selectedRegisteredAddressId"
        :show-registered-address-selector="showRegisteredAddressSelector"
        :countries="countries"
        :is-loading-countries="isLoadingCountries"
        :manual-sender-address="manualSenderAddress"
        :is-default-registered-address="isDefaultRegisteredAddress"
        :get-registered-address-title="getRegisteredAddressTitle"
        :get-registered-address-line="getRegisteredAddressLine"
        :get-registered-address-location="getRegisteredAddressLocation"
        :get-registered-address-phone="getRegisteredAddressPhone"
        @toggle-registered-selector="toggleRegisteredAddressSelector"
        @select-registered-address="selectRegisteredAddress"
        @ensure-countries-loaded="ensureCountriesLoaded"
        @manual-country-change="handleManualSenderCountryChange"
        @manual-address-change="handleManualSenderAddressChange"
        @open-postal-code-search="openSenderPostalCodeSearch"
      />

      <div class="d-flex justify-content-end mt-2">
        <b-button variant="outline-secondary" class="mr-1" @click="cancelSenderEditModal">
          {{ $t('Cancel') }}
        </b-button>
        <b-button variant="primary" @click="saveSenderEditModal">
          {{ $t('Save') }}
        </b-button>
      </div>
    </b-modal>

    <b-modal
      v-model="showProgressModal"
      hide-header
      hide-footer
      centered
      no-close-on-backdrop
      no-close-on-esc
      modal-class="bulk-import-modal"
    >
      <div class="position-relative p-1">
        <b-button
          v-if="!submitting"
          variant="link"
          class="position-absolute p-0 text-muted"
          style="top: -5px; right: -5px"
          @click="showProgressModal = false"
        >
          <feather-icon icon="XCircleIcon" size="24" />
        </b-button>

        <div class="d-flex align-items-center mb-2">
          <div
            class="bg-light-primary rounded p-1 mr-1 d-flex align-items-center justify-content-center"
          >
            <feather-icon icon="PackageIcon" size="24" class="text-primary" />
          </div>
          <div>
            <h4 class="mb-0 font-weight-bolder">{{ $t('Shipments are being created') }}</h4>
            <span class="text-muted font-weight-bold"
              >{{ completedCount }} / {{ submissionItems.length }} {{ $t('completed') }}</span
            >
          </div>
        </div>

        <b-progress
          :value="completedCount"
          :max="submissionItems.length"
          height="6px"
          variant="primary"
          class="mb-2"
        />

        <div class="d-flex flex-column pb-1" style="gap: 10px; max-height: 400px; overflow-y: auto">
          <div
            v-for="item in submissionItems"
            :key="item.id"
            class="d-flex align-items-center justify-content-between p-1 rounded"
            :class="{
              'border border-success': item.status === 'success',
              'border border-danger': item.status === 'error',
              'border border-primary': item.status === 'processing',
              'border border-light': item.status === 'pending'
            }"
          >
            <div class="d-flex align-items-center mr-1" style="min-width: 0">
              <div class="mr-1 flex-shrink-0 d-flex align-items-center">
                <feather-icon
                  v-if="item.status === 'success'"
                  icon="CheckCircleIcon"
                  class="text-success"
                />
                <feather-icon
                  v-else-if="item.status === 'error'"
                  icon="XCircleIcon"
                  class="text-danger"
                />
                <feather-icon
                  v-else-if="item.status === 'processing'"
                  icon="LoaderIcon"
                  class="text-primary"
                  animation="spin"
                />
                <feather-icon v-else icon="CircleIcon" class="text-muted" />
              </div>

              <div class="d-flex flex-column justify-content-center" style="min-width: 0">
                <span class="font-weight-bolder text-truncate">{{ item.ref }}</span>
                <small
                  v-if="item.status === 'error'"
                  class="text-danger mt-25"
                  style="line-height: 1.2; word-wrap: break-word; white-space: normal"
                >
                  {{ item.errorMessage }}
                </small>
              </div>
            </div>

            <div class="flex-shrink-0">
              <span v-if="item.status === 'success'" class="text-success small font-weight-bold">{{
                $t('Success')
              }}</span>
              <span
                v-else-if="item.status === 'error'"
                class="text-danger small font-weight-bold"
                >{{ $t('Error') }}</span
              >
              <span
                v-else-if="item.status === 'processing'"
                class="text-primary small font-weight-bold"
                >{{ $t('Creating...') }}</span
              >
              <span v-else class="text-muted small font-weight-bold">{{ $t('Waiting') }}</span>
            </div>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import vSelect from 'vue-select';
import {
  BButton,
  BAlert,
  BFormFile,
  BBadge,
  BFormCheckbox,
  BSpinner,
  BOverlay,
  BFormInput,
  BFormSelect,
  BFormTextarea,
  BModal,
  BProgress,
  BFormGroup
} from 'bootstrap-vue';
import * as XLSX from 'xlsx';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import useService from '@/auth/service/useService';
import PostalCodeSearchModal from '@/components/PostalCodeSearchModal.vue';
import { PROVIDERS, getProvider } from '@/libs/providerHelper';
import BulkShipmentSenderAddressEditor from '@/views/pages/consignments/BulkShipmentSenderAddressEditor.vue';

const BULK_IMPORT_SENDER_STORAGE_KEY = 'bulk-shipment-import-sender-address';
const CONSIGNOR_COLUMN_PREFIX = 'consignor_';

const HEADER_KEY_MAP = {
  'Referans Numarası': 'reference_no',
  'Hizmet Sağlayıcı Kodu': 'provider_code',
  'Incoterms Kodu': 'incoterms',
  'Alıcı Adı': 'consignee_name',
  'Alıcı Telefon Numarası': 'consignee_phone',
  'Alıcı Vergi Numarası': 'consignee_tax_number',
  'Alıcı EORI Numarası': 'consignee_eori_number',
  'Alıcı Ülke': 'consignee_country',
  'Alıcı Şehir/Bölge': 'consignee_city',
  'Alıcı Şehir/Bölge Kodu': 'consignee_state_code',
  'Alıcı Şehir/İlçe': 'consignee_district',
  'Alıcı Posta Kodu': 'consignee_postal_code',
  'Alıcı Adres': 'consignee_address',
  'Paket Tipi': 'package_type',
  'Paket Gönderi Tipi': 'post_type',
  'Paket Ağırlığı': 'weight',
  'Paket Uzunluk': 'length',
  'Paket Genişlik': 'width',
  'Paket Yükseklik': 'height',
  'Tehlikeli Madde Mi?': 'is_dangerous',
  'Ürün Açıklaması': 'item_description',
  'Ürün GTIP Kodu': 'gtip_code',
  'Ürün Menşei Ülke Kodu': 'item_origin_country',
  'Ürün Para Birimi': 'item_currency',
  'Ürün Birim Fiyatı': 'item_unit_price',
  'Ürün Adedi': 'item_quantity'
};

const MANDATORY_FIELDS = [
  'reference_no',
  'provider_code',
  'consignee_name',
  'consignee_phone',
  'consignee_country',
  'consignee_city',
  'consignee_district',
  'consignee_postal_code',
  'consignee_address',
  'package_type',
  'post_type',
  'weight',
  'length',
  'width',
  'height',
  'item_description',
  'gtip_code',
  'item_origin_country',
  'item_currency',
  'item_unit_price',
  'item_quantity',
  'incoterms'
];

const FIELD_LABELS = {
  provider_code: 'Service Provider',
  consignee_name: 'Recipient Name',
  consignee_phone: 'Phone',
  consignee_tax_number: 'Tax No',
  consignee_eori_number: 'EORI Number',
  consignee_country: 'Country',
  consignee_city: 'City/Region',
  consignee_district: 'District',
  consignee_postal_code: 'Postal Code',
  consignee_address: 'Address',
  package_type: 'Package Type',
  weight: 'Weight',
  length: 'Length',
  width: 'Width',
  height: 'Height',
  item_description: 'Item Description',
  gtip_code: 'HS Code',
  item_origin_country: 'Origin',
  item_currency: 'Currency',
  item_unit_price: 'Unit Price',
  item_quantity: 'Quantity',
  post_type: 'Shipment Type',
  incoterms: 'Incoterms',
  reference_no: 'Reference No'
};

const REVIEW_SECTIONS = [
  {
    key: 'provider',
    title: 'Service Provider',
    icon: 'TruckIcon',
    fields: [
      {
        key: 'provider_code',
        label: 'Service Provider',
        type: 'provider',
        required: true,
        colClass: 'col-lg-6 col-md-6'
      }
    ]
  },
  {
    key: 'consignee',
    title: 'Recipient Information',
    icon: 'UserIcon',
    fields: [
      { key: 'consignee_name', label: 'Recipient Name', required: true },
      { key: 'consignee_phone', label: 'Phone', required: true },
      { key: 'consignee_tax_number', label: 'Tax No (optional)' },
      { key: 'consignee_eori_number', label: 'EORI (optional)' },
      { key: 'consignee_country', label: 'Country', type: 'country', required: true },
      { key: 'consignee_city', label: 'City/Region', required: true },
      { key: 'consignee_district', label: 'District', required: true },
      { key: 'consignee_postal_code', label: 'Postal Code', required: true },
      {
        key: 'consignee_address',
        label: 'Address',
        type: 'textarea',
        required: true,
        colClass: 'col-12'
      }
    ]
  },
  {
    key: 'package',
    title: 'Package & Item Information',
    icon: 'BoxIcon',
    fields: [
      { key: 'package_type', label: 'Package Type', type: 'package_type', required: true },
      { key: 'weight', label: 'Weight', inputType: 'number', required: true },
      { key: 'length', label: 'Length', inputType: 'number', required: true },
      { key: 'width', label: 'Width', inputType: 'number', required: true },
      { key: 'height', label: 'Height', inputType: 'number', required: true },
      {
        key: 'item_description',
        label: 'Item Description',
        required: true,
        colClass: 'col-lg-6 col-md-6'
      },
      { key: 'gtip_code', label: 'HS Code', required: true },
      { key: 'item_origin_country', label: 'Origin', type: 'country', required: true },
      { key: 'item_currency', label: 'Currency', type: 'currency', required: true },
      { key: 'item_unit_price', label: 'Unit Price', inputType: 'number', required: true },
      { key: 'item_quantity', label: 'Quantity', inputType: 'number', required: true }
    ]
  },
  {
    key: 'shipment',
    title: 'Shipment Information',
    icon: 'FileTextIcon',
    fields: [
      { key: 'post_type', label: 'Shipment Type', type: 'post_type', required: true },
      { key: 'incoterms', label: 'Incoterms', type: 'incoterms', required: true },
      { key: 'reference_no', label: 'Reference No', required: true }
    ]
  }
];

const normalizeHeader = h =>
  String(h || '')
    .trim()
    .replace(/^\ufeff/, '')
    .replace(/\s+/g, ' ')
    .toLocaleLowerCase('tr');

const REVERSE_KEY_MAP = Object.entries(HEADER_KEY_MAP).reduce(
  (acc, [k, v]) => ({ ...acc, [v]: k }),
  {}
);

const NORMALIZED_MAP = Object.entries(HEADER_KEY_MAP).reduce(
  (acc, [k, v]) => ({ ...acc, [normalizeHeader(k)]: v }),
  {}
);

const createEmptySenderAddress = () => ({
  country: null,
  city: '',
  postal_code: '',
  district: '',
  address: '',
  attention_name: '',
  company_name: '',
  phone_number: '',
  email: '',
  tax_number: '',
  eori_number: '',
  ioss_number: ''
});

/* eslint-disable no-bitwise */
const buildCrcTable = () => {
  const table = new Uint32Array(256);

  for (let index = 0; index < 256; index += 1) {
    let current = index;

    for (let bit = 0; bit < 8; bit += 1) {
      current = current & 1 ? 0xedb88320 ^ (current >>> 1) : current >>> 1;
    }

    table[index] = current >>> 0;
  }

  return table;
};

const CRC_TABLE = buildCrcTable();
/* eslint-enable no-bitwise */

export default {
  name: 'BulkShipmentImportPage',
  components: {
    vSelect,
    BButton,
    BAlert,
    BFormFile,
    BBadge,
    BFormCheckbox,
    BSpinner,
    BOverlay,
    BFormInput,
    BFormSelect,
    BFormTextarea,
    BModal,
    BProgress,
    BFormGroup,
    PostalCodeSearchModal,
    BulkShipmentSenderAddressEditor
  },
  data() {
    return {
      currentStep: 1,
      showProgressModal: false,
      submissionItems: [],
      completedCount: 0,
      file: null,
      uploadError: null,
      isParsing: false,
      parsedRows: [],
      showOnlyErrors: false,
      headers: [],
      selectedRowIds: [],
      submitting: false,
      expandedRowId: null,
      isLoadingSenderStep: true,
      isLoadingCountries: false,
      hasLoadedRegisteredAddresses: false,
      senderAddressMode: 'registered',
      registeredAddressOptions: [],
      selectedRegisteredAddressId: null,
      showRegisteredAddressSelector: false,
      showSenderEditModal: false,
      senderEditSnapshot: null,
      senderEditCommitted: false,
      manualSenderAddress: createEmptySenderAddress(),
      manualSenderAddressManuallyEdited: false,
      providerOptions: [],
      packageTypeList: [],
      postTypeList: []
    };
  },
  computed: {
    countries() {
      return this.$store.getters['app/countries'] || [];
    },
    currencies() {
      return this.$store.getters['app/currencies'] || [];
    },
    selectedRegisteredAddress() {
      return (
        this.registeredAddressOptions.find(
          address => address.id === this.selectedRegisteredAddressId
        ) || null
      );
    },
    senderPostalCodeCountryIso2() {
      return this.resolveCountryOption(this.manualSenderAddress.country)?.iso2 || '';
    },
    activeSenderAddress() {
      if (this.senderAddressMode === 'registered') {
        return this.selectedRegisteredAddress
          ? this.normalizeRegisteredAddress(this.selectedRegisteredAddress)
          : null;
      }

      return this.normalizeManualSenderAddress(this.manualSenderAddress);
    },
    senderSummaryTitle() {
      if (!this.activeSenderAddress) return '-';
      return (
        this.activeSenderAddress.company_name || this.activeSenderAddress.attention_name || '-'
      );
    },
    senderSummaryDetails() {
      if (!this.activeSenderAddress) return '-';

      const addressLine = [
        this.activeSenderAddress.address,
        [this.activeSenderAddress.district, this.activeSenderAddress.city]
          .filter(Boolean)
          .join(', '),
        this.activeSenderAddress.postal_code,
        this.activeSenderAddress.country_name
      ]
        .filter(Boolean)
        .join(' · ');

      const contactLine = [
        this.activeSenderAddress.attention_name,
        this.activeSenderAddress.phone_number,
        this.activeSenderAddress.email
      ]
        .filter(Boolean)
        .join(' · ');

      return [addressLine, contactLine].filter(Boolean).join(' | ');
    },
    stepperSteps() {
      return [
        { number: 1, label: this.$t('Sender Address') },
        { number: 2, label: this.$t('Upload Excel') },
        { number: 3, label: this.$t('Review & Create') }
      ];
    },
    hasErrors() {
      return this.parsedRows.some(row => row.statusType === 'error');
    },
    displayedRows() {
      if (this.showOnlyErrors) {
        return this.parsedRows.filter(row => row.statusType === 'error');
      }

      return this.parsedRows;
    },
    reviewSections() {
      return REVIEW_SECTIONS;
    },
    providerSelectOptions() {
      return [
        { text: this.$t('Make a Choice'), value: '' },
        ...this.providerOptions.map(provider => ({
          text: provider.name || provider.text,
          value: String(provider.id || provider.value || '')
        }))
      ];
    },
    packageTypeOptions() {
      return [
        { text: this.$t('Make a Choice'), value: '' },
        ...this.packageTypeList.map(item => ({
          text: item.info || this.getPackageTypeName(item.value),
          value: String(item.value)
        }))
      ];
    },
    postTypeOptions() {
      return [
        { text: this.$t('Make a Choice'), value: '' },
        ...this.postTypeList.map(item => ({
          text: item.info || this.getPostTypeName(item.value),
          value: String(item.value)
        }))
      ];
    },
    currencyOptions() {
      return [
        { text: this.$t('Make a Choice'), value: '' },
        ...this.currencies.map(currency => ({
          text: `${currency.codeText || currency.code} ${
            currency.symbol ? `(${currency.symbol})` : ''
          }`.trim(),
          value: currency.code || ''
        }))
      ];
    },
    incotermsOptions() {
      return [
        { text: this.$t('Make a Choice'), value: '' },
        { text: 'DDP', value: '1' },
        { text: 'DDU', value: '2' },
        { text: 'DAP', value: '3' }
      ];
    },
    selectedRows() {
      return this.parsedRows.filter(row => this.selectedRowIds.includes(row.id));
    },
    creatableSelectedRows() {
      return this.selectedRows.filter(row => row.statusType !== 'success');
    },
    downloadableRows() {
      return this.parsedRows.filter(row => row.statusType === 'success' && row.base64label);
    },
    allDisplayedRowsSelected() {
      return (
        this.displayedRows.length > 0 &&
        this.displayedRows.every(row => this.selectedRowIds.includes(row.id))
      );
    },
    isSelectionPartial() {
      const selectedDisplayedCount = this.displayedRows.filter(row =>
        this.selectedRowIds.includes(row.id)
      ).length;

      return selectedDisplayedCount > 0 && selectedDisplayedCount < this.displayedRows.length;
    },
    visibleHeaders() {
      return this.headers.filter(header => !header.startsWith(CONSIGNOR_COLUMN_PREFIX));
    },
    columns() {
      return [];
    }
  },
  async created() {
    await Promise.all([this.initializeSenderStep(), this.initializeReviewOptions()]);
  },
  methods: {
    goToStep(stepNumber) {
      if (stepNumber < this.currentStep) {
        this.currentStep = stepNumber;
      }
    },
    resolveCountryOption(country) {
      if (!country) return null;

      const countryIso2 =
        country.iso2 || country.country_iso2 || (typeof country === 'string' ? country : '');
      const normalizedCountryIso = String(countryIso2 || '')
        .trim()
        .toUpperCase();

      if (!normalizedCountryIso) return null;

      return (
        this.countries.find(
          item =>
            String(item.iso2 || '')
              .trim()
              .toUpperCase() === normalizedCountryIso
        ) || null
      );
    },
    normalizeCountryFieldValue(value) {
      if (value === null || value === undefined) return '';

      const option = this.resolveCountryOption(value);
      if (option?.iso2) {
        return option.iso2;
      }

      return String(value).trim().toUpperCase();
    },
    normalizeCurrencyFieldValue(value) {
      if (value === null || value === undefined) return '';

      const normalizedCurrencyCode = String(value || '')
        .trim()
        .toUpperCase();

      const matchedCurrency = this.currencies.find(item => {
        const code = String(item.code || '')
          .trim()
          .toUpperCase();
        const codeText = String(item.codeText || '')
          .trim()
          .toUpperCase();

        return code === normalizedCurrencyCode || codeText === normalizedCurrencyCode;
      });

      if (matchedCurrency?.code) return matchedCurrency.code;

      return normalizedCurrencyCode;
    },
    normalizeImportedFieldValue(fieldKey, value) {
      if (value === null || value === undefined) return '';

      if (fieldKey === 'consignee_country' || fieldKey === 'item_origin_country') {
        return this.normalizeCountryFieldValue(value);
      }

      if (fieldKey === 'item_currency') {
        return this.normalizeCurrencyFieldValue(value);
      }

      return typeof value === 'string' ? value.trim() : value;
    },
    getStepStatus(stepNumber) {
      if (this.currentStep > stepNumber) return 'completed';
      if (this.currentStep === stepNumber) return 'active';
      return 'upcoming';
    },
    async initializeSenderStep() {
      this.isLoadingSenderStep = true;

      try {
        await Promise.allSettled([this.ensureCountriesLoaded(true), this.getAllAddressBookList()]);
        this.restoreSenderAddressSession();

        if (!this.selectedRegisteredAddressId && this.registeredAddressOptions.length) {
          const defaultAddress = this.resolveDefaultRegisteredAddress(
            this.registeredAddressOptions
          );
          this.selectedRegisteredAddressId =
            defaultAddress?.id || this.registeredAddressOptions[0].id;
        }

        this.senderAddressMode = this.registeredAddressOptions.length ? 'registered' : 'manual';

        this.persistSenderAddressSession();
      } finally {
        this.isLoadingSenderStep = false;
      }
    },
    async initializeReviewOptions() {
      try {
        await this.$store.dispatch('app/getCurrencies');
      } catch (error) {
        // Currency list falls back to existing store state if request fails.
      }

      const [providersResponse, packageTypesResponse, postTypesResponse] = await Promise.allSettled(
        [
          useService.getAllProviders({ paginated: false }),
          useService.getConsignmentPackageType(),
          useService.getConsignmentPostType()
        ]
      );

      this.providerOptions =
        providersResponse.status === 'fulfilled' && Array.isArray(providersResponse.value?.data)
          ? providersResponse.value.data
          : PROVIDERS;

      this.packageTypeList =
        packageTypesResponse.status === 'fulfilled' &&
        Array.isArray(packageTypesResponse.value?.data)
          ? packageTypesResponse.value.data
          : [
              { value: 1, info: this.$t('Box') },
              { value: 2, info: this.$t('Envelope') }
            ];

      this.postTypeList =
        postTypesResponse.status === 'fulfilled' && Array.isArray(postTypesResponse.value?.data)
          ? postTypesResponse.value.data
          : [
              { value: 4, info: this.$t('Sale') },
              { value: 3, info: this.$t('Gift') },
              { value: 1, info: this.$t('Sample') }
            ];
    },
    async ensureCountriesLoaded(force = false) {
      if (!force && this.countries.length) return;

      this.isLoadingCountries = true;

      try {
        await this.$store.dispatch('app/getCountries');
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('Warning'),
            icon: 'AlertTriangleIcon',
            text: this.$t('No country data'),
            variant: 'warning'
          }
        });
      } finally {
        this.isLoadingCountries = false;
      }
    },
    async getAllAddressBookList() {
      this.hasLoadedRegisteredAddresses = false;

      try {
        const response = await useService.getAllWarehouses({
          page: 1,
          limit: 1000
        });

        this.registeredAddressOptions = (response.data || []).map(item => ({
          ...item,
          text: item.name,
          value: item.id
        }));
      } catch (error) {
        this.registeredAddressOptions = [];
      } finally {
        this.hasLoadedRegisteredAddresses = true;
      }
    },
    resolveDefaultRegisteredAddress(addresses) {
      const defaultFlags = [
        'isDefault',
        'is_default',
        'default',
        'isPrimary',
        'is_primary',
        'primary'
      ];

      return (
        addresses.find(address =>
          defaultFlags.some(flag => [true, 1, '1', 'true'].includes(address?.[flag]))
        ) || addresses[0]
      );
    },
    isDefaultRegisteredAddress(address) {
      if (!address) return false;
      return (
        this.resolveDefaultRegisteredAddress(this.registeredAddressOptions || [])?.id === address.id
      );
    },
    restoreSenderAddressSession() {
      const rawState = sessionStorage.getItem(BULK_IMPORT_SENDER_STORAGE_KEY);

      if (!rawState) return;

      try {
        const savedState = JSON.parse(rawState);
        const savedMode = savedState?.senderAddressMode;
        const savedRegisteredAddressId = savedState?.selectedRegisteredAddressId;
        const savedManualAddress = savedState?.manualSenderAddress;
        const savedManualEdited = savedState?.manualSenderAddressManuallyEdited;

        if (savedMode === 'registered' || savedMode === 'manual') {
          this.senderAddressMode = savedMode;
        }

        if (
          savedRegisteredAddressId &&
          this.registeredAddressOptions.some(address => address.id === savedRegisteredAddressId)
        ) {
          this.selectedRegisteredAddressId = savedRegisteredAddressId;
        }

        if (savedManualAddress && typeof savedManualAddress === 'object') {
          this.manualSenderAddress = this.deserializeManualSenderAddress(savedManualAddress);
        }

        if (typeof savedManualEdited === 'boolean') {
          this.manualSenderAddressManuallyEdited = savedManualEdited;
        }
      } catch (error) {
        sessionStorage.removeItem(BULK_IMPORT_SENDER_STORAGE_KEY);
      }
    },
    deserializeManualSenderAddress(savedAddress) {
      const defaultState = createEmptySenderAddress();

      return {
        ...defaultState,
        ...savedAddress,
        country: this.resolveCountryOption(savedAddress.country)
      };
    },
    handleManualSenderCountryChange(country) {
      this.manualSenderAddress.country = this.resolveCountryOption(country);
      this.manualSenderAddressManuallyEdited = true;
      this.persistSenderAddressSession();
    },
    handleManualSenderAddressChange() {
      this.manualSenderAddressManuallyEdited = true;
      this.persistSenderAddressSession();
    },
    applySelectedRegisteredAddressToManualAddress() {
      if (!this.selectedRegisteredAddress) return;

      this.manualSenderAddress = this.convertRegisteredAddressToManualAddress(
        this.selectedRegisteredAddress
      );
      this.manualSenderAddressManuallyEdited = false;
    },
    persistSenderAddressSession() {
      sessionStorage.setItem(
        BULK_IMPORT_SENDER_STORAGE_KEY,
        JSON.stringify({
          senderAddressMode: this.senderAddressMode,
          selectedRegisteredAddressId: this.selectedRegisteredAddressId,
          manualSenderAddress: this.serializeManualSenderAddress(this.manualSenderAddress),
          manualSenderAddressManuallyEdited: this.manualSenderAddressManuallyEdited
        })
      );
    },
    serializeManualSenderAddress(address) {
      return {
        ...address,
        country: address.country
          ? {
              value: address.country.value,
              text: address.country.text,
              iso2: address.country.iso2
            }
          : null
      };
    },
    switchSenderAddressMode(mode) {
      this.senderAddressMode = mode;

      if (
        mode === 'manual' &&
        (this.isManualSenderAddressEmpty() || !this.manualSenderAddressManuallyEdited) &&
        this.selectedRegisteredAddress
      ) {
        this.applySelectedRegisteredAddressToManualAddress();
      }

      this.persistSenderAddressSession();
    },
    isManualSenderAddressEmpty() {
      return !Object.entries(this.manualSenderAddress).some(([key, value]) => {
        if (key === 'country') return !!value;
        return String(value || '').trim() !== '';
      });
    },
    convertRegisteredAddressToManualAddress(address) {
      const normalizedAddress = this.normalizeRegisteredAddress(address);
      const country = this.resolveCountryOption({
        value: normalizedAddress.country_id,
        text: normalizedAddress.country_name,
        iso2: normalizedAddress.country_iso2
      });

      return {
        country,
        city: normalizedAddress.city,
        postal_code: normalizedAddress.postal_code,
        district: normalizedAddress.district,
        address: normalizedAddress.address,
        attention_name: normalizedAddress.attention_name,
        company_name: normalizedAddress.company_name,
        phone_number: normalizedAddress.phone_number,
        email: normalizedAddress.email,
        tax_number: normalizedAddress.tax_number,
        eori_number: normalizedAddress.eori_number,
        ioss_number: normalizedAddress.ioss_number
      };
    },
    normalizeRegisteredAddress(address) {
      if (!address) return null;

      return {
        address_book_id: address.id,
        company_name: address.name || '',
        attention_name: address.attentionName || '',
        phone_number: address.phoneNumber || '',
        email: address.email || '',
        tax_number: address.tax_no || address.taxNumber || address.taxNo || '',
        eori_number: address.eori_number || address.eoriNumber || address.eori_no || '',
        ioss_number: address.ioss_number || address.iossNumber || address.iossNo || '',
        country_id: address.country?.id || null,
        country_name: address.country?.name || '',
        country_iso2: address.country?.iso2 || '',
        city: address.state?.name || '',
        district: address.city?.name || '',
        postal_code: address.postalCode?.zipcode || '',
        address: address.address || ''
      };
    },
    normalizeManualSenderAddress(address) {
      if (!address) return null;

      const selectedCountry = this.resolveCountryOption(address.country);

      return {
        address_book_id: null,
        company_name: String(address.company_name || '').trim(),
        attention_name: String(address.attention_name || '').trim(),
        phone_number: String(address.phone_number || '').trim(),
        email: String(address.email || '').trim(),
        tax_number: String(address.tax_number || '').trim(),
        eori_number: String(address.eori_number || '').trim(),
        ioss_number: String(address.ioss_number || '').trim(),
        country_id: selectedCountry?.value || null,
        country_name: selectedCountry?.text || '',
        country_iso2: selectedCountry?.iso2 || '',
        city: String(address.city || '').trim(),
        district: String(address.district || '').trim(),
        postal_code: String(address.postal_code || '').trim(),
        address: String(address.address || '').trim()
      };
    },
    selectRegisteredAddress(addressId) {
      this.selectedRegisteredAddressId = addressId;
      this.showRegisteredAddressSelector = false;
      this.persistSenderAddressSession();
    },
    toggleRegisteredAddressSelector() {
      this.showRegisteredAddressSelector = !this.showRegisteredAddressSelector;
    },
    getRegisteredAddressTitle(address) {
      return address?.name || '-';
    },
    getRegisteredAddressLine(address) {
      return address?.address || '-';
    },
    getRegisteredAddressLocation(address) {
      const parts = [
        address?.city?.name,
        address?.state?.name,
        address?.postalCode?.zipcode
      ].filter(Boolean);

      return parts.join(' ') || '-';
    },
    getRegisteredAddressPhone(address) {
      return address?.phoneNumber || '-';
    },
    openSenderPostalCodeSearch() {
      if (!this.senderPostalCodeCountryIso2) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('Warning'),
            icon: 'AlertTriangleIcon',
            text: this.$t('Please select the country first'),
            variant: 'warning'
          }
        });
        return;
      }

      this.$refs.postalCodeSearchModal.show();
    },
    handleSenderPostalCodeSelect(item) {
      this.manualSenderAddress.postal_code = item.postalCode || '';
      this.manualSenderAddress.city = item.state || '';
      this.manualSenderAddress.district = item.city || '';

      if (item.place) {
        this.manualSenderAddress.address = item.place;
      }

      this.manualSenderAddressManuallyEdited = true;
      this.persistSenderAddressSession();
    },
    async handleSenderStepContinue() {
      if (this.senderAddressMode === 'registered') {
        if (!this.selectedRegisteredAddress) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: this.$t('Warning'),
              icon: 'AlertTriangleIcon',
              text: this.$t('Please select a sender address'),
              variant: 'warning'
            }
          });
          return;
        }

        this.persistSenderAddressSession();
        this.currentStep = 2;
        return;
      }

      const isValid = await this.$refs.senderStepEditor.validateManualForm();

      if (!isValid) {
        return;
      }

      this.persistSenderAddressSession();
      this.currentStep = 2;
    },
    openSenderEditModal() {
      this.senderEditSnapshot = {
        senderAddressMode: this.senderAddressMode,
        selectedRegisteredAddressId: this.selectedRegisteredAddressId,
        showRegisteredAddressSelector: this.showRegisteredAddressSelector,
        manualSenderAddress: this.serializeManualSenderAddress(this.manualSenderAddress),
        manualSenderAddressManuallyEdited: this.manualSenderAddressManuallyEdited
      };
      this.senderEditCommitted = false;
      this.showRegisteredAddressSelector = false;
      this.showSenderEditModal = true;
    },
    cancelSenderEditModal() {
      this.showSenderEditModal = false;
    },
    async saveSenderEditModal() {
      if (this.senderAddressMode === 'registered') {
        if (!this.selectedRegisteredAddress) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: this.$t('Warning'),
              icon: 'AlertTriangleIcon',
              text: this.$t('Please select a sender address'),
              variant: 'warning'
            }
          });
          return;
        }
      } else {
        const isValid = await this.$refs.senderEditModalEditor.validateManualForm();
        if (!isValid) return;
      }

      this.persistSenderAddressSession();
      this.senderEditCommitted = true;
      this.showSenderEditModal = false;
    },
    handleSenderEditModalHidden() {
      if (!this.senderEditCommitted && this.senderEditSnapshot) {
        this.senderAddressMode = this.senderEditSnapshot.senderAddressMode;
        this.selectedRegisteredAddressId = this.senderEditSnapshot.selectedRegisteredAddressId;
        this.showRegisteredAddressSelector = this.senderEditSnapshot.showRegisteredAddressSelector;
        this.manualSenderAddress = this.deserializeManualSenderAddress(
          this.senderEditSnapshot.manualSenderAddress || {}
        );
        this.manualSenderAddressManuallyEdited =
          this.senderEditSnapshot.manualSenderAddressManuallyEdited;
      } else {
        this.showRegisteredAddressSelector = false;
      }

      this.senderEditSnapshot = null;
      this.senderEditCommitted = false;
    },
    getProviderDisplayName(providerCode) {
      const matchedProvider =
        this.providerOptions.find(
          provider => String(provider.id || provider.value) === String(providerCode)
        ) ||
        getProvider(Number(providerCode)) ||
        getProvider(providerCode);

      return matchedProvider?.name || providerCode || '-';
    },
    getCountryDisplayName(countryValue) {
      const option = this.resolveCountryOption(countryValue);
      return option?.text || countryValue || '';
    },
    getCountrySelectValue(countryValue) {
      return this.resolveCountryOption(countryValue);
    },
    getCountrySubmissionValue(countryOption) {
      return countryOption?.iso2 || '';
    },
    getPackageTypeName(type) {
      const value = Number(type);
      if (value === 1) return this.$t('Box');
      if (value === 2) return this.$t('Envelope');
      if (value === 3) return this.$t('Pallet');
      if (value === 4) return this.$t('Tube');
      if (value === 5) return this.$t('Other');
      if (value === 6) return this.$t('Contract');
      return type || '-';
    },
    getPostTypeName(type) {
      const value = Number(type);
      if (value === 1) return this.$t('Sample');
      if (value === 2) return this.$t('Micro');
      if (value === 3) return this.$t('Gift');
      if (value === 4) return this.$t('Sale');
      if (value === 5) return this.$t('Personal');
      return type || '-';
    },
    getIncotermsName(type) {
      const map = {
        1: 'DDP',
        2: 'DDU',
        3: 'DAP'
      };

      return map[Number(type)] || type || '-';
    },
    getStatusBadgeVariant(row) {
      if (row.statusType === 'success') return 'success';
      if (row.statusType === 'error') return 'danger';
      if (row.statusType === 'processing') return 'warning';
      return 'secondary';
    },
    getStatusBadgeText(row) {
      if (row.statusType === 'success') return this.$t('Successful');
      if (row.statusType === 'error') return this.$t('Error');
      if (row.statusType === 'processing') return this.$t('Creating');
      return this.$t('Waiting');
    },
    formatWeight(weight) {
      if (weight === null || weight === undefined || String(weight).trim() === '') return '-';
      return `${weight} kg`;
    },
    toggleRowPanel(rowId) {
      this.expandedRowId = this.expandedRowId === rowId ? null : rowId;
    },
    isRowSelected(rowId) {
      return this.selectedRowIds.includes(rowId);
    },
    toggleRowSelection(rowId, checked) {
      if (checked) {
        if (!this.selectedRowIds.includes(rowId)) {
          this.selectedRowIds = [...this.selectedRowIds, rowId];
        }
        return;
      }

      this.selectedRowIds = this.selectedRowIds.filter(id => id !== rowId);
    },
    selectAllDisplayedRows() {
      this.selectedRowIds = [
        ...new Set([...this.selectedRowIds, ...this.displayedRows.map(row => row.id)])
      ];
    },
    clearSelection() {
      this.selectedRowIds = [];
    },
    toggleSelectAllDisplayed(checked) {
      if (checked) {
        this.selectAllDisplayedRows();
        return;
      }

      const displayedIds = this.displayedRows.map(row => row.id);
      this.selectedRowIds = this.selectedRowIds.filter(id => !displayedIds.includes(id));
    },
    normalizeSelectValue(value) {
      if (value === null || value === undefined || value === '') return '';
      return String(value);
    },
    normalizeCurrencyValue(value) {
      return this.normalizeCurrencyFieldValue(value);
    },
    getRowFieldClass(row, fieldKey) {
      return row.fieldErrors?.[fieldKey] ? 'is-invalid' : '';
    },
    getRowFieldError(row, fieldKey) {
      return row.fieldErrors?.[fieldKey] || '';
    },
    setRowPendingState(row) {
      row.statusType = 'pending';
      row.status = this.$t('Waiting');
      row.errorSource = null;
      row.errorColumns = [];
    },
    handleCountryFieldChange(row, fieldKey, option) {
      this.handleRowFieldChange(row, fieldKey, this.getCountrySubmissionValue(option));
    },
    handleRowFieldChange(row, fieldKey, value) {
      let normalizedValue = value;

      if (fieldKey === 'consignee_country' || fieldKey === 'item_origin_country') {
        normalizedValue = this.normalizeCountryFieldValue(value);
      } else if (fieldKey === 'item_currency') {
        normalizedValue = this.normalizeCurrencyFieldValue(value);
      }

      row[fieldKey] = normalizedValue;

      if (row.fieldErrors?.[fieldKey]) {
        this.$delete(row.fieldErrors, fieldKey);
      }

      row.errorColumns = Object.keys(row.fieldErrors || {});

      if (fieldKey === 'reference_no') {
        this.validateRows(this.parsedRows, { keepSuccess: true });
        return;
      }

      if (!Object.keys(row.fieldErrors || {}).length && row.statusType === 'error') {
        this.setRowPendingState(row);
      }
    },
    downloadLabel(row) {
      if (!row.base64label) return;

      const fileName = `label_${row.reference_no}.${this.getLabelFileExtension(row.contentType)}`;
      this.downloadBlob(this.base64ToBlob(row.base64label, row.contentType), fileName);
    },
    async submitRow(row) {
      const targetRow = this.parsedRows.find(item => item.id === row.id);
      if (!targetRow) return;

      targetRow.hasAttempted = true;

      const isValid = this.validateSingleRow(targetRow);
      if (!isValid) return;

      targetRow.statusType = 'processing';
      targetRow.status = this.$t('Processing...');

      try {
        const response = await useService.createConsignment(this.createShipmentPayload(targetRow));
        targetRow.status = this.$t('Success');
        targetRow.statusType = 'success';
        targetRow.base64label = response?.data?.base64label;
        targetRow.contentType = response?.data?.contentType;
        targetRow.errorColumns = [];
        targetRow.fieldErrors = {};
        this.selectedRowIds = this.selectedRowIds.filter(id => id !== targetRow.id);

        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('Success'),
            icon: 'CheckIcon',
            text: `1 ${this.$t('shipments created successfully.')}`,
            variant: 'success'
          }
        });
      } catch (error) {
        let message = this.$t('Error');
        const errors = Array.isArray(error) ? error : error.response?.data?.errors;

        if (errors && Array.isArray(errors)) {
          const parsedMessage = this.parseErrors(errors, targetRow);
          message =
            error.response?.data?.message && !Array.isArray(error)
              ? parsedMessage || error.response.data.message
              : parsedMessage || message;
        } else if (error.response?.data?.message) {
          message = error.response.data.message;
        } else if (error.message) {
          message = error.message;
        } else if (typeof error === 'string') {
          message = error;
        }

        targetRow.status = message;
        targetRow.statusType = 'error';
        targetRow.errorSource = 'server';

        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('Warning'),
            icon: 'AlertTriangleIcon',
            text: `1 ${this.$t('shipments failed.')}`,
            variant: 'warning'
          }
        });
      }
    },
    prevStep() {
      if (this.currentStep > 1) this.currentStep -= 1;
    },
    downloadTemplate() {
      const link = document.createElement('a');
      link.href = '/assets/c2h-toplu-sevkiyat.xlsx';
      link.download = 'c2h-toplu-sevkiyat.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    handleFile(event) {
      const selectedFile = event.dataTransfer?.files?.[0] || event.target?.files?.[0] || this.file;
      if (selectedFile) this.processFile(selectedFile);
    },
    clearFile() {
      Object.assign(this, {
        file: null,
        uploadError: null,
        parsedRows: [],
        selectedRowIds: [],
        expandedRowId: null
      });
    },
    processFile(file) {
      if (!file) return;

      this.uploadError = null;
      this.isParsing = true;
      this.file = null;

      const fileExtension = file.name ? file.name.split('.').pop().toLowerCase() : '';
      if (fileExtension !== 'xlsx') {
        this.uploadError = this.$t('Only .xlsx files are supported.');
        this.isParsing = false;
        return;
      }

      const reader = new FileReader();
      reader.onload = event =>
        setTimeout(() => {
          try {
            this.parseExcelData(new Uint8Array(event.target.result), file);
          } catch (error) {
            this.uploadError = this.$t('File could not be read. Format might be corrupted.');
          } finally {
            this.isParsing = false;
          }
        }, 50);

      reader.readAsArrayBuffer(file);
    },
    parseExcelData(data, file) {
      const workbook = XLSX.read(data, { type: 'array' });

      const firstSheetName = workbook.SheetNames[0];
      const sheetData = XLSX.utils.sheet_to_json(workbook.Sheets[firstSheetName], {
        header: 1,
        defval: ''
      });

      const rawHeaders = sheetData[0];
      if (!rawHeaders || rawHeaders.length === 0) {
        this.uploadError = this.$t('Headers could not be found in the first row.');
        return;
      }

      const expectedHeaders = Object.keys(HEADER_KEY_MAP);
      const normalizedRawHeaders = rawHeaders.map(normalizeHeader);
      const normalizedExpectedHeaders = expectedHeaders.map(normalizeHeader);

      const errorDetails = expectedHeaders.filter((_, i) =>
        normalizedRawHeaders.length < expectedHeaders.length
          ? !normalizedRawHeaders.includes(normalizedExpectedHeaders[i])
          : normalizedRawHeaders[i] !== normalizedExpectedHeaders[i]
      );

      if (errorDetails.length > 0) {
        const details = errorDetails.join(', ');
        this.uploadError = `${this.$t(
          'A-Z columns and first-row headers cannot be changed or deleted.'
        )} (${this.$t('Missing or invalid')}: ${details})`;
        return;
      }

      const fileData = sheetData
        .slice(1)
        .filter(row => row && row.length > 0 && row.some(cell => cell !== ''));

      if (fileData.length > 100) {
        this.uploadError = this.$t('Maximum 100 shipments can be uploaded at once');
        return;
      }

      const { headerIndexMap, foundDtoKeys } = this.mapHeadersKeys(rawHeaders);

      this.parsedRows = fileData.map((row, id) => {
        const rowObject = {
          id,
          status: '',
          statusType: '',
          errorColumns: [],
          fieldErrors: {},
          errorSource: null,
          hasAttempted: false
        };
        Object.entries(headerIndexMap).forEach(([columnIndex, dtoKey]) => {
          rowObject[dtoKey] = this.normalizeImportedFieldValue(dtoKey, row[columnIndex]);
        });
        return rowObject;
      });

      this.validateRows(this.parsedRows);

      this.headers = [...new Set(foundDtoKeys)];
      this.selectedRowIds = [];
      this.expandedRowId = null;
      this.file = file;
    },
    validateRows(rows, options = {}) {
      const referenceCounts = this.getReferenceCounts(rows);

      rows.forEach(rowObject => {
        const localErrors = this.getRowValidationErrors(rowObject, referenceCounts);
        const mergedErrors = { ...localErrors };

        Object.keys(rowObject.fieldErrors || {}).forEach(key => {
          const msg = rowObject.fieldErrors[key];
          if (
            msg !== this.$t('This field is required') &&
            msg !== this.$t('Reference number must be unique')
          ) {
            mergedErrors[key] = msg;
          }
        });

        this.applyRowValidationState(rowObject, mergedErrors, options);
      });
    },
    validateSingleRow(row) {
      const referenceCounts = this.getReferenceCounts(this.parsedRows);
      const fieldErrors = this.getRowValidationErrors(row, referenceCounts);
      this.applyRowValidationState(row, fieldErrors, { keepSuccess: true });
      return !Object.keys(row.fieldErrors || {}).length;
    },
    getReferenceCounts(rows) {
      return rows.reduce((counts, row) => {
        const ref = String(row.reference_no || '').trim();
        if (ref) {
          counts[ref] = (counts[ref] || 0) + 1;
        }
        return counts;
      }, {});
    },
    getRowValidationErrors(rowObject, referenceCounts = {}) {
      const fieldErrors = {};

      MANDATORY_FIELDS.forEach(fieldKey => {
        const value = rowObject[fieldKey];
        if (value === null || value === undefined || String(value).trim() === '') {
          fieldErrors[fieldKey] = this.$t('This field is required');
        }
      });

      const ref = String(rowObject.reference_no || '').trim();
      if (ref && referenceCounts[ref] > 1) {
        fieldErrors.reference_no = this.$t('Reference number must be unique');
      }

      return fieldErrors;
    },
    applyRowValidationState(rowObject, fieldErrors, options = {}) {
      rowObject.fieldErrors = fieldErrors;
      rowObject.errorColumns = Object.keys(fieldErrors);

      if (rowObject.errorColumns.length) {
        rowObject.statusType = 'error';

        const hasServerErrors = rowObject.errorColumns.some(key => {
          const msg = rowObject.fieldErrors[key];
          return (
            msg !== this.$t('This field is required') &&
            msg !== this.$t('Reference number must be unique')
          );
        });

        if (!hasServerErrors || rowObject.errorSource !== 'server') {
          rowObject.status = this.$t('Check the fields');
          rowObject.errorSource = 'validation';
        }
      } else if (!options.keepSuccess || rowObject.statusType !== 'success') {
        this.setRowPendingState(rowObject);
      }
    },
    mapHeadersKeys(rawHeaders) {
      const headerIndexMap = {};
      const foundDtoKeys = [];

      rawHeaders.map(normalizeHeader).forEach((normalizedHeader, index) => {
        if (NORMALIZED_MAP[normalizedHeader]) {
          headerIndexMap[index] = NORMALIZED_MAP[normalizedHeader];
          foundDtoKeys.push(NORMALIZED_MAP[normalizedHeader]);
        }
      });

      return { headerIndexMap, foundDtoKeys };
    },
    confirmRemoveRow(rowId) {
      this.$bvModal
        .msgBoxConfirm(this.$t('Are you sure you want to delete this shipment?'), {
          title: this.$t('Delete Shipment'),
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: this.$t('Delete'),
          cancelTitle: this.$t('Cancel'),
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true
        })
        .then(value => {
          if (value) {
            this.removeRow(rowId);
          }
        });
    },
    removeRow(rowId) {
      this.parsedRows = this.parsedRows.filter(row => row.id !== rowId);
      this.selectedRowIds = this.selectedRowIds.filter(id => id !== rowId);
      if (this.expandedRowId === rowId) {
        this.expandedRowId = null;
      }
      this.validateRows(this.parsedRows, { keepSuccess: true });

      if (!this.parsedRows.length) {
        this.currentStep = 2;
      }
    },
    createConsignorPayload() {
      const senderAddress = this.activeSenderAddress;

      if (!senderAddress) {
        throw new Error(this.$t('Sender address is required'));
      }

      return {
        name: senderAddress.company_name,
        attention_name: senderAddress.attention_name,
        attention_email: senderAddress.email,
        phone_number: senderAddress.phone_number,
        tax_number: senderAddress.tax_number || undefined,
        eori_number: senderAddress.eori_number || undefined,
        address: senderAddress.address,
        postal_code: senderAddress.postal_code,
        country: senderAddress.country_id || senderAddress.country_name,
        state: senderAddress.city,
        city: senderAddress.district
      };
    },
    createShipmentPayload(row) {
      const payload = {
        consignor: this.createConsignorPayload(),
        consignee: {
          name: row.consignee_name,
          phone_number: row.consignee_phone,
          tax_number: row.consignee_tax_number,
          eori_number:
            row.consignee_eori_number != null
              ? String(row.consignee_eori_number)
              : row.consignee_eori_number,
          address: row.consignee_address,
          postal_code:
            row.consignee_postal_code != null
              ? String(row.consignee_postal_code)
              : row.consignee_postal_code,
          city: row.consignee_district,
          state: row.consignee_city,
          country: this.normalizeCountryFieldValue(row.consignee_country),
          state_code: row.consignee_state_code
        },
        packages: [
          {
            weight: Number(row.weight),
            width: Number(row.width),
            height: Number(row.height),
            length: Number(row.length),
            package_type: Number(row.package_type),
            post_type: Number(row.post_type),
            count: 1,
            ...(row?.item_quantity
              ? {
                  items: [
                    {
                      description: row.item_description,
                      quantity: Number(row.item_quantity),
                      unit_price: Number(row.item_unit_price),
                      currency_code: this.normalizeCurrencyFieldValue(row.item_currency),
                      gtip: row.gtip_code != null ? String(row.gtip_code) : row.gtip_code,
                      origin: this.normalizeCountryFieldValue(row.item_origin_country)
                    }
                  ]
                }
              : {})
          }
        ],
        reference_no: row.reference_no,
        provider_id: Number(row.provider_code),
        preference: 0,
        address_resolver_type: 0,
        ...(row.incoterms ? { incoterms_type: Number(row.incoterms) } : {})
      };

      if (this.activeSenderAddress?.ioss_number) {
        payload.ioss_number = this.activeSenderAddress.ioss_number;
      }

      return payload;
    },
    mapErrorPathToColumn(path) {
      if (!path) return null;

      const normalizedPath = path
        .replace(/\[(\d+)\]/g, '.$1')
        .replace(/([A-Z])/g, '_$1')
        .toLowerCase();

      if (normalizedPath === 'reference_no') return 'reference_no';
      if (normalizedPath === 'provider_id') return 'provider_code';
      if (normalizedPath === 'incoterms_type') return 'incoterms';

      const parts = normalizedPath.split('.');
      if (parts[0] === 'consignee' || parts[0] === 'consignor') {
        const prefix = parts[0];
        const field = parts[1];
        const map = {
          name: 'name',
          phone_number: 'phone',
          tax_number: 'tax_number',
          eori_number: 'eori_number',
          address: 'address',
          postal_code: 'postal_code',
          city: 'district',
          state: 'city',
          country: 'country',
          state_code: 'state_code'
        };

        if (map[field]) return `${prefix}_${map[field]}`;
      }

      if (parts[0] === 'packages' && parts[1] === '0') {
        const field = parts[2];
        if (['weight', 'width', 'height', 'length', 'package_type', 'post_type'].includes(field)) {
          return field;
        }

        if (field === 'items' && parts[3] === '0') {
          const itemField = parts[4];
          const map = {
            description: 'description',
            quantity: 'quantity',
            unit_price: 'unit_price',
            currency: 'currency',
            currency_code: 'currency',
            gtip: 'gtip_code',
            origin: 'origin_country'
          };

          if (map[itemField]) return `item_${map[itemField]}`;
        }
      }

      return null;
    },
    parseErrors(errors, parsedRow) {
      if (!Array.isArray(errors)) return '';

      if (parsedRow) {
        parsedRow.errorColumns = [];
        parsedRow.fieldErrors = {};
      }

      return errors
        .map(error => {
          const rawField = error.property || error.field;
          let prefix = '';

          if (rawField) {
            const column = this.mapErrorPathToColumn(rawField);
            if (column && parsedRow) {
              parsedRow.fieldErrors[column] = error.message || this.$t('Check the fields');
              if (!parsedRow.errorColumns.includes(column)) {
                parsedRow.errorColumns.push(column);
              }
            }

            const fieldName = column
              ? this.$t(FIELD_LABELS[column] || REVERSE_KEY_MAP[column] || rawField)
              : rawField;
            prefix = `${fieldName} - `;
          }

          return `${prefix}${error.message || ''}`;
        })
        .filter(Boolean)
        .join(' | ');
    },
    getLabelFileExtension(contentType) {
      return contentType && contentType.startsWith('image/') ? contentType.split('/')[1] : 'pdf';
    },
    base64ToBytes(base64) {
      const sanitized = String(base64 || '').replace(/\s/g, '');
      const binary = window.atob(sanitized);
      const bytes = new Uint8Array(binary.length);

      for (let index = 0; index < binary.length; index += 1) {
        bytes[index] = binary.charCodeAt(index);
      }

      return bytes;
    },
    base64ToBlob(base64, contentType) {
      return new Blob([this.base64ToBytes(base64)], {
        type: contentType || 'application/pdf'
      });
    },
    downloadBlob(blob, fileName) {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    encodeUtf8(value) {
      return new window.TextEncoder().encode(String(value));
    },
    /* eslint-disable no-bitwise */
    getCrc32(bytes) {
      let crc = 0xffffffff;

      for (let index = 0; index < bytes.length; index += 1) {
        crc = CRC_TABLE[(crc ^ bytes[index]) & 0xff] ^ (crc >>> 8);
      }

      return (crc ^ 0xffffffff) >>> 0;
    },
    /* eslint-enable no-bitwise */
    createStoredZip(files) {
      const localParts = [];
      const centralParts = [];
      let offset = 0;
      let centralSize = 0;

      files.forEach(file => {
        const fileNameBytes = this.encodeUtf8(file.name);
        const fileBytes = file.bytes;
        const crc = this.getCrc32(fileBytes);

        const localHeader = new Uint8Array(30 + fileNameBytes.length);
        const localView = new DataView(localHeader.buffer);
        localView.setUint32(0, 0x04034b50, true);
        localView.setUint16(4, 20, true);
        localView.setUint16(6, 0, true);
        localView.setUint16(8, 0, true);
        localView.setUint16(10, 0, true);
        localView.setUint16(12, 0, true);
        localView.setUint32(14, crc, true);
        localView.setUint32(18, fileBytes.length, true);
        localView.setUint32(22, fileBytes.length, true);
        localView.setUint16(26, fileNameBytes.length, true);
        localView.setUint16(28, 0, true);
        localHeader.set(fileNameBytes, 30);

        const centralHeader = new Uint8Array(46 + fileNameBytes.length);
        const centralView = new DataView(centralHeader.buffer);
        centralView.setUint32(0, 0x02014b50, true);
        centralView.setUint16(4, 20, true);
        centralView.setUint16(6, 20, true);
        centralView.setUint16(8, 0, true);
        centralView.setUint16(10, 0, true);
        centralView.setUint16(12, 0, true);
        centralView.setUint16(14, 0, true);
        centralView.setUint32(16, crc, true);
        centralView.setUint32(20, fileBytes.length, true);
        centralView.setUint32(24, fileBytes.length, true);
        centralView.setUint16(28, fileNameBytes.length, true);
        centralView.setUint16(30, 0, true);
        centralView.setUint16(32, 0, true);
        centralView.setUint16(34, 0, true);
        centralView.setUint16(36, 0, true);
        centralView.setUint32(38, 0, true);
        centralView.setUint32(42, offset, true);
        centralHeader.set(fileNameBytes, 46);

        localParts.push(localHeader, fileBytes);
        centralParts.push(centralHeader);

        offset += localHeader.length + fileBytes.length;
        centralSize += centralHeader.length;
      });

      const endRecord = new Uint8Array(22);
      const endView = new DataView(endRecord.buffer);
      endView.setUint32(0, 0x06054b50, true);
      endView.setUint16(4, 0, true);
      endView.setUint16(6, 0, true);
      endView.setUint16(8, files.length, true);
      endView.setUint16(10, files.length, true);
      endView.setUint32(12, centralSize, true);
      endView.setUint32(16, offset, true);
      endView.setUint16(20, 0, true);

      return new Blob([...localParts, ...centralParts, endRecord], {
        type: 'application/zip'
      });
    },
    downloadAllLabels() {
      if (!this.downloadableRows.length) return;

      const files = this.downloadableRows.map(row => ({
        name: `label_${row.reference_no}.${this.getLabelFileExtension(row.contentType)}`,
        bytes: this.base64ToBytes(row.base64label)
      }));

      this.downloadBlob(this.createStoredZip(files), 'bulk-labels.zip');
    },
    async submitSelected() {
      if (!this.creatableSelectedRows.length) return;

      this.submissionItems = this.creatableSelectedRows.map(row => ({
        id: row.id,
        ref: row.reference_no,
        status: 'pending',
        errorMessage: ''
      }));
      this.completedCount = 0;
      this.showProgressModal = true;
      this.submitting = true;

      let successCount = 0;
      let errorCount = 0;

      this.creatableSelectedRows.forEach(row => {
        const parsedRow = this.parsedRows.find(item => item.id === row.id);
        if (parsedRow) {
          parsedRow.fieldErrors = parsedRow.fieldErrors || {};
        }
      });

      for (const [index, row] of this.creatableSelectedRows.entries()) {
        const submissionItem = this.submissionItems[index];
        const parsedRow = this.parsedRows.find(item => item.id === row.id);
        if (parsedRow) parsedRow.hasAttempted = true;
        const isValid = parsedRow ? this.validateSingleRow(parsedRow) : false;

        if (!isValid) {
          errorCount += 1;
          submissionItem.status = 'error';
          submissionItem.errorMessage = this.$t('Please correct the required fields');
        } else {
          submissionItem.status = 'processing';
          if (parsedRow) {
            parsedRow.statusType = 'processing';
            parsedRow.status = this.$t('Processing...');
          }

          try {
            const response = await useService.createConsignment(this.createShipmentPayload(row));
            submissionItem.status = 'success';

            if (parsedRow) {
              parsedRow.status = this.$t('Success');
              parsedRow.statusType = 'success';
              parsedRow.base64label = response?.data?.base64label;
              parsedRow.contentType = response?.data?.contentType;
              parsedRow.fieldErrors = {};
              parsedRow.errorColumns = [];
            }

            successCount += 1;
          } catch (error) {
            errorCount += 1;
            let message = this.$t('Error');
            const errors = Array.isArray(error) ? error : error.response?.data?.errors;

            if (errors && Array.isArray(errors)) {
              const parsedMessage = this.parseErrors(errors, parsedRow);
              message =
                error.response?.data?.message && !Array.isArray(error)
                  ? parsedMessage || error.response.data.message
                  : parsedMessage || message;
            } else if (error.response?.data?.message) {
              message = error.response.data.message;
            } else if (error.message) {
              message = error.message;
            } else if (typeof error === 'string') {
              message = error;
            }

            submissionItem.status = 'error';
            submissionItem.errorMessage = message;

            if (parsedRow) {
              parsedRow.status = message;
              parsedRow.statusType = 'error';
              parsedRow.errorSource = 'server';
            }
          }
        }

        this.completedCount += 1;
      }

      this.submitting = false;
      this.selectedRowIds = this.selectedRowIds.filter(id => {
        const row = this.parsedRows.find(item => item.id === id);
        return row && row.statusType !== 'success';
      });

      const notify = (count, title, icon, text, variant) => {
        if (count <= 0) return;

        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t(title),
            icon,
            text: `${count} ${this.$t(text)}`,
            variant
          }
        });
      };

      notify(successCount, 'completed', 'CheckIcon', 'shipments created successfully.', 'success');
      notify(errorCount, 'Warning', 'AlertTriangleIcon', 'shipments failed.', 'warning');
    }
  }
};
</script>

<style lang="scss">
.progress-stepper-wrap {
  width: 100%;
  max-width: 1900px;
  overflow-x: auto;
}

.progress-stepper {
  display: flex;
  align-items: center;
  min-width: 900px;
}

.progress-stepper__segment {
  display: flex;
  align-items: center;
  flex: 1 1 auto;
}

.progress-stepper__segment:last-child {
  flex: 0 0 auto;
}

.progress-stepper__item {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
}

.progress-stepper__badge {
  width: 34px;
  height: 34px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.95rem;
  font-weight: 700;
}

.progress-stepper__badge--completed {
  background: #19a754;
  color: #fff;
}

.progress-stepper__badge--active {
  background: #3467eb;
  color: #fff;
}

.progress-stepper__badge--upcoming {
  background: #edf2fa;
  color: #9aa8bd;
}

.progress-stepper__label {
  font-size: 0.95rem;
  white-space: nowrap;
}

.progress-stepper__label--completed {
  color: #53627b;
  font-weight: 500;
}

.progress-stepper__label--active {
  color: #1f2a44;
  font-weight: 700;
}

.progress-stepper__label--upcoming {
  color: #6d7c95;
  font-weight: 500;
}

.progress-stepper__line {
  height: 2px;
  min-width: 120px;
  flex: 1 1 180px;
  margin: 0 1rem;
  background: #e8eef7;
  border-radius: 999px;
}

.progress-stepper__line--completed {
  background: #19a754;
}

.sender-step__header {
  padding: 1.25rem 1.25rem 1rem;
  border-bottom: 1px solid #e9edf5;
}

.sender-step__icon {
  width: 58px;
  height: 58px;
  border-radius: 16px;
  background: #eef4ff;
}

.sender-step__header h2 {
  font-size: 1.45rem;
  line-height: 1.2;
}

.sender-step__header p {
  font-size: 0.95rem;
}

.sender-mode-toggle {
  display: inline-flex;
  padding: 0.3rem;
  border-radius: 14px;
  background: #eef2f9;
  gap: 0.35rem;
}

.sender-mode-toggle__button {
  border: 0;
  background: transparent;
  padding: 0.65rem 1.2rem;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 600;
  color: #5b6885;
  transition: all 0.2s ease;
}

.sender-mode-toggle__button--active {
  background: #fff;
  color: #1f2a44;
  box-shadow: 0 4px 12px rgba(31, 42, 68, 0.12);
}

.sender-step__body {
  padding: 1.25rem;
}

.sender-step__footer {
  padding: 1.25rem;
  border-top: 1px solid #e9edf5;
}

.registered-address-card {
  border: 1px solid #b7d3ff;
  border-radius: 18px;
  background: #eff5ff;
  padding: 1.1rem 1.2rem;
}

.registered-address-card__icon {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: rgba(54, 92, 227, 0.12);
}

.registered-address-card__line {
  color: #52627e;
  font-size: 0.9rem;
}

.registered-address-list {
  display: grid;
  gap: 0.75rem;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.registered-address-option {
  width: 100%;
  border: 1px solid #d8e3f5;
  border-radius: 14px;
  background: #fff;
  padding: 0.85rem 1rem;
  transition: all 0.2s ease;
  min-height: 100%;
}

.registered-address-option--active {
  border-color: #365ce3;
  box-shadow: 0 0 0 3px rgba(54, 92, 227, 0.08);
}

.registered-address-card h3,
.registered-address-option .font-weight-bolder {
  font-size: 1.1rem;
  line-height: 1.3;
}

.registered-address-option .text-muted {
  font-size: 0.88rem;
}

.manual-sender-form__section-title {
  margin-bottom: 1rem;
  font-weight: 700;
  font-size: 1rem;
}

.manual-sender-form label {
  font-size: 0.88rem;
}

.manual-sender-form .form-control,
.manual-sender-form ::v-deep .vs__selected,
.manual-sender-form ::v-deep .vs__search,
.manual-sender-form ::v-deep .vs__dropdown-toggle {
  font-size: 0.92rem;
}

.sender-country-row__action {
  min-width: 120px;
  font-size: 0.88rem;
}

.table tbody + tbody {
  border-top: none;
}

.sender-optional-divider {
  padding-top: 14px;
  border-top: 1px solid #e9edf5;
}

.bulk-import-info-danger {
  border: 1px solid #ea5556;
  background: #fff7dd;
  color: #a85d00;
}

.bulk-import-info-warning {
  border: 1px solid #ffd362;
  background: #fff7dd;
  color: #a85d00;
}

.sender-summary-card,
.review-toolbar {
  border: 1px solid #dce5f4;
  border-radius: 14px;
  background: #fff;
}

.sender-summary-card {
  padding: 12px 18px;
}

.sender-summary-card .font-weight-bolder {
  font-size: 0.98rem;
}

.sender-summary-card .text-muted,
.sender-summary-card small,
.review-toolbar small {
  font-size: 0.85rem;
}

.review-toolbar__inner {
  padding: 14px 18px;
}

.review-toolbar__back {
  transform: scale(1.15);
}

.bulk-soft-button {
  background: #f5f7fb;
  border-color: #d7dfeb;
  color: #53627b;
}

.bulk-soft-button:hover,
.bulk-soft-button:focus,
.bulk-soft-button:active,
.bulk-soft-button:not(:disabled):not(.disabled):active {
  background: #e9eef7;
  border-color: #c3cede;
  color: #1f2a44;
}

.bulk-review-table th,
.bulk-review-table td {
  vertical-align: middle;
}

.bulk-review-table thead th {
  background: #f7faff;
  border-bottom: 1px solid #dce5f4;
  color: #6d7c95;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  white-space: nowrap;
}

.bulk-review-table__checkbox-cell {
  width: 52px;
  text-align: center;
}

.bulk-review-table__toggle-cell {
  width: 42px;
}

.bulk-review-row {
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.bulk-review-row:hover {
  background: #f8fbff;
}

.bulk-review-row--expanded {
  background: #f8fbff;
}

.bulk-review-row--error {
  background: #fff8f8;
}

.bulk-review-details-row td {
  padding: 0 !important;
  background: #fbfdff;
  border-top: 0;
}

.bulk-review-details {
  padding: 1.25rem;
}

.bulk-review-section + .bulk-review-section {
  margin-top: 1.5rem;
}

.bulk-review-section__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #e9edf5;
}

.bulk-review-details ::v-deep .vs__dropdown-toggle,
.bulk-review-details .form-control,
.bulk-review-details .custom-select,
.bulk-review-details textarea {
  min-height: 2.75rem;
}

.bulk-review-details textarea {
  min-height: 88px;
}

.dark-layout .bulk-shipment-import,
.dark-layout .bulk-import-modal {
  .sender-step__header,
  .sender-step__footer,
  .sender-optional-divider {
    border-color: rgba(255, 255, 255, 0.08);
  }

  .sender-step__icon,
  .sender-mode-toggle,
  .registered-address-card,
  .registered-address-option,
  .bulk-review-details-row td {
    background: rgba(255, 255, 255, 0.04);
  }

  .sender-mode-toggle__button {
    color: #c3cad9;
  }

  .sender-mode-toggle__button--active {
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
  }

  .registered-address-option {
    .text-muted {
      color: #c3cad9 !important;
    }
  }

  .registered-address-card,
  .registered-address-option,
  .sender-summary-card,
  .review-toolbar {
    border-color: rgba(115, 103, 240, 0.2);
  }

  .registered-address-option--active {
    border-color: #7367f0 !important;
    box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.2) !important;
  }

  .progress-stepper__label--active {
    color: #d0d2d6;
  }

  .bulk-import-info-danger,
  .bulk-import-info-warning {
    background: rgba(255, 255, 255, 0.04);
    color: #c3cad9;
  }

  .bulk-soft-button {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(115, 103, 240, 0.18);
    color: #d0d7e6;
  }

  .bulk-soft-button:hover,
  .bulk-soft-button:focus,
  .bulk-soft-button:active,
  .bulk-soft-button:not(:disabled):not(.disabled):active {
    background: rgba(115, 103, 240, 0.12);
    border-color: rgba(115, 103, 240, 0.28);
    color: #ffffff;
  }

  .registered-address-card__line {
    color: #c3cad9;
  }

  .bulk-review-row:hover,
  .bulk-review-row--expanded {
    background: rgba(115, 103, 240, 0.08);
  }

  .bulk-review-row--error {
    background: rgba(234, 84, 85, 0.12);
  }

  .custom-overlay-bg > div:first-child {
    background-color: #161d31 !important;
  }
}

.custom-overlay-bg > div:first-child {
  background-color: #ffffff !important;
}

@media (max-width: 991.98px) {
  .progress-stepper {
    min-width: 820px;
  }

  .progress-stepper__line {
    min-width: 72px;
    margin: 0 0.75rem;
  }

  .sender-step__header,
  .sender-step__body,
  .sender-step__footer {
    padding: 1rem;
  }

  .sender-mode-toggle {
    width: 100%;
  }

  .sender-mode-toggle__button {
    flex: 1;
    padding-inline: 1rem;
  }

  .sender-country-row {
    flex-direction: column;
  }

  .sender-country-row__action {
    width: 100%;
    margin-top: 0.75rem;
    margin-left: 0 !important;
  }

  .registered-address-list {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 767.98px) {
  .registered-address-list {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
